import { CloseOutlined } from "@ant-design/icons";
import { Button, Checkbox, Flex, Typography } from "antd";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "../../../assets/css/setting.css";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HTextArea from "../../../components/custom/input/textarea/HTextArea";
import { AppContext } from "../../../context/AppContext";
import EmailComponent from "../../Setting/EmailComponent";
import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";
import type { ActionNodeData } from "../nodes/ActionNode";
import { useWorkflowStore } from "../store";
import { selectSelectedStep, selectSelectedStepIndex } from "../store/selectors";
import { getActionIconAndClass, getFormTypeForAction } from "../utils/actionCodeMapping";
import { ACTION_HELP_CONTENT } from "../utils/helpContentConfig";
import { AutomatedResponseForm } from "./AutomatedResponseForm";
import { ConfigPanelFooter } from "./ConfigPanelFooter";

const { Text } = Typography;

export interface ActionConfigPanelProps {
    actionTypes: WorkflowEnumItem[];
}

const ActionTypePicker: React.FC<{
    actionTypes: WorkflowEnumItem[];
    onSelect: (code: string, title: string) => void;
}> = ({ actionTypes, onSelect }) => {
    const groups = useMemo(() => {
        const byGroup = new Map<string, WorkflowEnumItem[]>();
        for (const at of actionTypes ?? []) {
            const group = at.meta?.group ?? "Other";
            const arr = byGroup.get(group);
            if (arr) arr.push(at);
            else byGroup.set(group, [at]);
        }
        for (const arr of byGroup.values()) {
            arr.sort((a, b) => a.order - b.order);
        }
        return Array.from(byGroup.entries()).map(([label, items]) => ({
            label,
            actions: items,
        }));
    }, [actionTypes]);

    return (
        <div className="action-picker">
            {groups.map((group) => (
                <div key={group.label} className="action-picker__group">
                    <div className="hs-fs-12 hs-color-light-grey-2">{group.label}</div>
                    {group.actions.map((action) => {
                        const { icon } = getActionIconAndClass(action.code);
                        return (
                            <div
                                key={action.code}
                                className="action-picker__card"
                                onClick={() => onSelect(action.code, action.idleLabel)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") onSelect(action.code, action.idleLabel);
                                }}
                            >
                                <div className="action-picker__icon rounded-circle">{icon}</div>
                                <div className="action-picker__text">
                                    <span className="action-picker__title">{action.idleLabel}</span>
                                    <span className="action-picker__desc">
                                        {action.meta?.description ?? action.idleLabel}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const DEFAULT_EMAIL_NOTIFICATION_MESSAGE =
    "You have received a new notification. Please check your account for details.";

const NotifyUserForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => {
    const [appProperties] = useContext(AppContext);
    const [emailErrorObj, setEmailErrorObj] = useState<Record<string, string> | null>(null);

    const textAreaRef = useRef<{ resizableTextArea?: { textArea?: HTMLTextAreaElement } }>(null);

    const emailField = useMemo(
        () => ({
            key: "additionalEmails",
            placeholder: "Enter an email address",
            onchange: (_parentKey: string, _key: string, value: string[]) => {
                update({ additionalEmails: value });
            },
        }),
        [update],
    );

    React.useEffect(() => {
        if (!data.notificationTypes?.includes("Email")) {
            update({ notificationTypes: ["Email"] });
        }
    }, [data.notificationTypes, update]);

    React.useEffect(() => {
        if (data.notifyUser != null && data.recordOwner == null && data.accountOwner == null) {
            const legacy = data.notifyUser;
            update({
                recordOwner: legacy === "Record Owner",
                accountOwner: legacy === "Account Owner",
                includeAdditionalEmails: legacy === "Additional Email Addresses",
                additionalEmails: legacy === "Additional Email Addresses" ? data.additionalEmails ?? [] : [],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time migration from legacy notifyUser
    }, [data.notifyUser, data.recordOwner, data.accountOwner]);

    const handleRecordOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ recordOwner: e.target.checked });
        setEmailErrorObj(null);
    };
    const handleAccountOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ accountOwner: e.target.checked });
        setEmailErrorObj(null);
    };
    const handleAdditionalEmailsChange = (e: { target: { checked: boolean } }) => {
        update({
            includeAdditionalEmails: e.target.checked,
            additionalEmails: e.target.checked ? data.additionalEmails ?? [] : [],
        });
        setEmailErrorObj(null);
    };

    const showAdditionalEmailsSelect = data.includeAdditionalEmails ?? false;

    return (
        <>
            <div className="px-1">
                <div className="trigger-rule-config-panel__notification-type">
                    <Text className="hs-fs-14 hs-color-BlackRussian">Send Email to</Text>
                </div>
            </div>
            <div className="px-1 d-flex flex-column gap-3 mt-2">
                <Checkbox
                    className="schedule-check-box hs-fs-16"
                    checked={data.recordOwner ?? false}
                    onChange={handleRecordOwnerChange}
                >
                    Record Owner
                </Checkbox>
                <Checkbox
                    className="schedule-check-box hs-fs-16"
                    checked={data.accountOwner ?? false}
                    onChange={handleAccountOwnerChange}
                >
                    Account Owner ({appProperties?.extensionInstalledUserEmailId || ""})
                </Checkbox>
                <Checkbox
                    className="schedule-check-box hs-fs-16"
                    checked={showAdditionalEmailsSelect}
                    onChange={handleAdditionalEmailsChange}
                >
                    Additional Email Addresses
                </Checkbox>
            </div>
            {showAdditionalEmailsSelect && (
                <div className="px-1 py-3 m-1 mt-3 mt-sm-2">
                    <EmailComponent
                        parentKey="workflowNotify"
                        emailList={data.additionalEmails ?? []}
                        field={emailField}
                        setErrorObj={setEmailErrorObj}
                    />
                    {emailErrorObj?.["Email notification"] && (
                        <div className="hs-fs-12 hs-color-red mt-1">{emailErrorObj["Email notification"]}</div>
                    )}
                </div>
            )}
            <div className="px-1 py-3">
                <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">
                    Message
                </Text>
                <HTextArea
                    ref={textAreaRef}
                    value={data.notifyMessage ?? DEFAULT_EMAIL_NOTIFICATION_MESSAGE}
                    readOnly
                    className="trigger-rule-config-panel__textarea w-100"
                    rows={4}
                    autoSize={{ minRows: 4 }}
                />
            </div>
        </>
    );
};

/** Keys aligned with settingConfig.js loggingPreference */
const ADD_NOTE_FIELDS = [
    {
        key: "logRecordAsThread",
        label: "Merge all activities into a single activity as notes?",
        options: [
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
        ],
    },
    {
        key: "logRecordsFor",
        label: "Where would you prefer to log received message?",
        options: [
            { label: "All matching records", value: "all" },
            { label: "Recent record", value: "recent" },
        ],
    },
];

const AddNoteForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 py-3">
        {ADD_NOTE_FIELDS.map((field) => (
            <div key={field.key} className="mb-3">
                <HRadioGroup
                    groupLabel={field.label}
                    options={field.options}
                    radioGroupProps={{
                        value: data[field.key as keyof ActionNodeData] ?? field.options[0].value,
                        onChange: (e: { target: { value: string } }) =>
                            update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                        className: "schedule-card-group",
                    }}
                    radioProps={{ className: "schedule-card" }}
                />
            </div>
        ))}
    </div>
);

/** Keys aligned with settingConfig.js loggingPreference */
const LOG_ACTIVITY_FIELDS = [
    {
        key: "logRecordAsThread",
        label: "Merge all activities into a single activity as notes?",
        options: [
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
        ],
    },
    {
        key: "logRecordsFor",
        label: "Where would you prefer to log received message?",
        options: [
            { label: "All matching records", value: "all" },
            { label: "Recent record", value: "recent" },
        ],
    },
];

const LogActivityForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 py-3">
        {LOG_ACTIVITY_FIELDS.map((field) => (
            <div key={field.key} className="mb-3">
                <HRadioGroup
                    groupLabel={field.label}
                    options={field.options}
                    radioGroupProps={{
                        value: data[field.key as keyof ActionNodeData] ?? field.options[0].value,
                        onChange: (e: { target: { value: string } }) =>
                            update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                        className: "schedule-card-group",
                    }}
                    radioProps={{ className: "schedule-card" }}
                />
            </div>
        ))}
    </div>
);

const CREATE_LEAD_DESCRIPTION =
    "Enable this option to automatically create a new lead in your CRM whenever a message or call is received from a number that isn't linked to any existing contact or lead.";

const CreateLeadForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = () => (
    <div className="px-1 py-3">
        <Text className="hs-fs-12 hs-color-dark-grey">{CREATE_LEAD_DESCRIPTION}</Text>
    </div>
);

const getFormForType = (actionTypeOrCode: string, data: ActionNodeData, update: (d: Partial<ActionNodeData>) => void) => {
    const formType = getFormTypeForAction(actionTypeOrCode);
    switch (formType) {
        case "AutomatedResponse":
            return <AutomatedResponseForm data={data} update={update} />;
        case "NotifyUser":
            return <NotifyUserForm data={data} update={update} />;
        case "CreateLead":
            return <CreateLeadForm data={data} update={update} />;
        case "AddNote":
            return <AddNoteForm data={data} update={update} />;
        case "LogActivity":
            return <LogActivityForm data={data} update={update} />;
        default:
            return (
                <div className="px-1 py-3">
                    <Text className="trigger-rule-config-panel__hint hs-fs-12 hs-color-mediumDark">
                        Configuration for this action type is not yet available.
                    </Text>
                </div>
            );
    }
};

export const ActionConfigPanel: React.FC<ActionConfigPanelProps> = ({ actionTypes }) => {
    const selectedStep = useWorkflowStore(selectSelectedStep);
    const selectedStepIndex = useWorkflowStore(selectSelectedStepIndex);
    const updateStepData = useWorkflowStore((s) => s.updateStepData);
    const addStep = useWorkflowStore((s) => s.addStep);
    const selectNode = useWorkflowStore((s) => s.selectNode);

    const nodeId = selectedStep?.id ?? "";
    const persistedData = useMemo(
        () => ((selectedStep?.data as ActionNodeData) ?? {}),
        [selectedStep?.data]
    );
    const [initialData, setInitialData] = useState<ActionNodeData>(persistedData);
    const [draftData, setDraftData] = useState<ActionNodeData>(persistedData);
    const [showHelp, setShowHelp] = useState(false);
    const prevNodeIdRef = useRef(nodeId);

    useEffect(() => {
        if (prevNodeIdRef.current !== nodeId) {
            prevNodeIdRef.current = nodeId;
            setShowHelp(false);
        }
        setInitialData(persistedData);
        setDraftData(persistedData);
    }, [nodeId, persistedData]);

    const updateDraft = (partial: Partial<ActionNodeData>) => {
        setDraftData((prev) => ({ ...prev, ...partial }));
    };

    const commitDraft = () => {
        if (!nodeId) return;
        updateStepData(nodeId, draftData);
        setInitialData(draftData);
    };

    const hasDraftChanges = JSON.stringify(initialData) !== JSON.stringify(draftData);
    const isPlaceholderStep = (persistedData.isPlaceholder ?? false) || (!persistedData.actionType && !persistedData.actionCode);
    const canCommit = Boolean(draftData.actionCode || draftData.actionType);
    const canAddStep = canCommit;
    const canSaveChanges = hasDraftChanges;

    const handleClose = () => selectNode(null);
    const showPicker =
        !persistedData.actionType &&
        !persistedData.actionCode &&
        !draftData.actionType &&
        !draftData.actionCode;

    const handleSelectActionType = (code: string, title: string) => {
        const formType = getFormTypeForAction(code);
        const partial: Partial<ActionNodeData> = {
            actionCode: code,
            actionType: formType,
            title,
            isPlaceholder: false,
        };
        if (formType === "AddNote" || formType === "LogActivity") {
            partial.logRecordAsThread = "false";
            partial.logRecordsFor = "all";
        }
        updateDraft(partial);
    };

    const handlePrimaryClick = () => {
        if (isPlaceholderStep) {
            if (!canAddStep || selectedStepIndex < 0 || !selectedStep) return;
            commitDraft();
            addStep(selectedStepIndex + 1, selectedStep.type as "condition" | "action");
            return;
        }
        if (!canSaveChanges) return;
        commitDraft();
    };

    if (showPicker) {
        return (
            <div className="trigger-rule-config-panel">
                <div className="trigger-rule-config-panel__header">
                    <Text className="trigger-rule-config-panel__title hs-fw-600 hs-fs-16 hs-color-BlackRussian">
                        Actions
                    </Text>
                    <Button
                        type="text"
                        className="trigger-rule-config-panel__close"
                        onClick={handleClose}
                        aria-label="Close"
                        icon={<CloseOutlined />}
                    />
                </div>
                <div className="trigger-rule-config-panel__body py-3 px-3">
                    <ActionTypePicker actionTypes={actionTypes} onSelect={handleSelectActionType} />
                </div>
            </div>
        );
    }

    const displayTitle = draftData.title ?? draftData.actionType ?? draftData.actionCode;
    const formType = getFormTypeForAction(draftData.actionCode ?? draftData.actionType ?? "");
    const helpContent = ACTION_HELP_CONTENT[formType] ?? ACTION_HELP_CONTENT.default;

    return (
        <div className="trigger-rule-config-panel">
            <div className="trigger-rule-config-panel__header">
                <Flex align="center" gap={8}>
                    <Text className="trigger-rule-config-panel__title hs-fw-600 hs-fs-16 hs-color-BlackRussian">
                        {displayTitle}
                    </Text>
                    <div
                        className="automationIconsSprite help-icon cursor-pointer"
                        onClick={() => setShowHelp(!showHelp)}
                        role="button"
                    />
                </Flex>
                <Button
                    type="text"
                    className="trigger-rule-config-panel__close"
                    onClick={handleClose}
                    aria-label="Close"
                    icon={<CloseOutlined />}
                />
            </div>
            <div className="trigger-rule-config-panel__body py-3 px-3">
                {showHelp && (
                    <div>
                        <Text className="hs-fs-14 hs-color-mediumDark">
                            How it works
                        </Text>
                        <Text className="hs-fs-12 hs-color-mediumDark d-block mt-1">
                            {helpContent.body}
                        </Text>
                    </div>
                )}
                {getFormForType(draftData.actionCode ?? draftData.actionType ?? "", draftData, updateDraft)}
            </div>
            <ConfigPanelFooter
                primaryLabel={isPlaceholderStep ? "Add Step" : "Save Changes"}
                primaryDisabled={isPlaceholderStep ? !canAddStep : !canSaveChanges}
                onPrimaryClick={handlePrimaryClick}
            />
        </div>
    );
};
